import { useState } from "react";
import { fetchAuditLog } from "../api/client";
import { Alert } from "../components/Alert";
import { Spinner } from "../components/Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";

const LIMIT_OPTIONS = [50, 100, 200, 500, 1000] as const;

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function AuditScreen(): JSX.Element {
  const [limit, setLimit] = useState<number>(200);
  const {
    data: entries,
    loading,
    error,
    reload,
  } = useAsyncData(() => fetchAuditLog(limit), [limit]);

  return (
    <div className="audit-screen">
      <div className="audit-screen__controls">
        <label htmlFor="audit-limit">Show</label>
        <select
          id="audit-limit"
          value={limit}
          onChange={(event) => setLimit(Number(event.target.value))}
        >
          {LIMIT_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} entries
            </option>
          ))}
        </select>
        <button type="button" className="button button--secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      {loading && <Spinner label="Loading audit log…" />}

      {error && <Alert variant="error">Could not load the audit log: {formatError(error)}</Alert>}

      {!loading && !error && (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Actor</th>
              <th scope="col">Action</th>
              <th scope="col">Target</th>
              <th scope="col">Result</th>
              <th scope="col">Detail</th>
            </tr>
          </thead>
          <tbody>
            {(entries ?? []).map((entry, index) => (
              <tr key={`${entry.at}-${index}`}>
                <td>{formatDate(entry.at)}</td>
                <td>
                  <code>{entry.actor}</code>
                </td>
                <td>{entry.action}</td>
                <td>{entry.target || "—"}</td>
                <td>
                  <span className={`badge badge--${entry.succeeded ? "ready" : "attention"}`}>
                    {entry.succeeded ? "Succeeded" : "Failed"}
                  </span>
                </td>
                <td>
                  {Object.keys(entry.detail).length > 0 ? (
                    <code>{JSON.stringify(entry.detail)}</code>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {(entries ?? []).length === 0 && (
              <tr>
                <td colSpan={6}>No audit entries yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
